import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099BInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099-B instructions for Tax Year 2025"
          description="Learn how to fill out Form 1099-B for Tax Year 2025 with step-by-step guidance from TaxZerone to accurately report broker and barter transactions."
          keywords=""
          canonical="resources/information-returns/form-1099-b-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099-B</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-b-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-B at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1099-B,{' '}
                        </strong>{' '}
                        which brokers and barter exchanges use to report
                        proceeds from sales or exchanges of securities,
                        commodities, investment property, or barter transactions
                        to the IRS.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Recipient copies must be furnished by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 15
                        </strong>
                        , and the form must be filed with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          February 28
                        </strong>{' '}
                        if filing on paper or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          March 31{' '}
                        </strong>{' '}
                        if filing electronically.{' '}
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          This article covers
                        </strong>{' '}
                        line-by-line instructions, including payer and recipient
                        details, CUSIP numbers, FATCA checkbox, Form 8949 codes,
                        proceeds, cost basis, wash sale losses, gain/loss type,
                        collectibles/QOF reporting, tax withheld, noncovered
                        securities, basis reporting, bartering, and state
                        information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late or incorrect filing penalties range from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per form
                        </strong>
                        , depending on how late the form is filed or whether
                        there is intentional disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  IRS Form 1099-B, Proceeds from Broker and Barter Exchange
                  Transactions, is a tax document used to report the sale or
                  exchange of securities or investment properties by brokers and
                  barter exchanges. It details the proceeds from these
                  transactions, including the sale date, cost basis, and any
                  resulting gain or loss.
                </Typography>

                {/* <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the 1099-NEC accurately.
                </Alert> */}

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 1099-B?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Brokers and barter exchanges are required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-B
                  </Link>
                  . Specifically:
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b>Brokers:</b> Any broker who sells stocks, bonds,
                      commodities, or other financial instruments on behalf of a
                      customer must file Form 1099-B to report the proceeds from
                      these sales to both the IRS and the customer.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <b> Barter exchanges:</b> Organizations that facilitate
                      the exchange of goods or services between members must
                      file Form 1099-B to report the fair market value of goods
                      or services exchanged.
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-B due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must be aware of two important deadlines related to
                  Form 1099-B:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Filing deadline with the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The deadline for payers to submit Form 1099-B to the IRS
                  depends on the filing method:
                </Typography>

                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>
                      Electronic filing deadline: March 31, 2026
                    </span>
                    <br /> If you are e-filing Form 1099-B with the IRS, the
                    deadline is March 31st of the year following the tax year
                    for which the form is being issued.
                  </li>
                  <Box
                    component="div"
                    sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    className="gradient-bg"
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box sx={{ width: '100%' }}>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Avoid the stress of last-minute 1099-B deadlines.
                        </Typography>
                        <Typography variant="body2">
                          E-file with TaxZerone now for hassle-free IRS
                          compliance and peace of mind.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          maxWidth: 150,
                          textAlign: 'center',
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            boxShadow: 'none',
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            color: '#333',
                            '&:hover, &:focus, &:active': { color: '#fff' },
                          }}
                          size="large"
                          href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099binstructions`}
                        >
                          E-File Now
                        </Button>
                      </Box>
                    </Stack>
                  </Box>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}>
                      Paper filing deadline: February 28, 2026*
                    </span>
                    <br /> If you are filing Form 1099-B with the IRS using
                    traditional paper methods, the deadline is February 28th of
                    the year following the tax year for which the form is
                    issued.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Recipient copies deadline: February 15, 2026*
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers must provide recipients with copies of Form 1099-B by
                  February 15th of the year following the tax year for which the
                  form is issued. This ensures recipients have the necessary
                  information to accurately file their tax returns on time.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> * if the deadline falls on weekend or federal
                  holiday, it is extended to next business days.
                </Alert>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Failure to meet these deadlines or providing inaccurate
                  information on Form 1099-B may result in IRS penalties. Payers
                  should take appropriate measures to ensure the timely and
                  accurate filing and distribution of Form 1099-B to avoid
                  potential penalties.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-B penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you miss filing Form 1099-B within the deadline, the IRS
                  will impose a penalty. Here are the Form 1099-B penalty rates:
                </Typography>
                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Stack
                  direction={{ md: 'row', xs: 'column' }}
                  spacing={2}
                  alignItems="center"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '20px !important',
                      }}
                    >
                      Why risk penalties?
                    </Typography>
                    <Typography variant="body2">
                      E-file your 1099-B forms ahead of the deadline with
                      TaxZerone.
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 198,
                      textAlign: 'left',
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099binstructions`}
                    >
                      E-file Form 1099-B
                    </Button>
                  </Box>
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-B Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-B.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/1099-b-payer-details-img.webp" />
                  <source srcSet="/assets/images/1099-b-payer-details-img.png" />
                  <img
                    src="/assets/images/1099-b-payer-details-img.png"
                    loading="lazy"
                    alt="Form 1099-b-payer details"
                    width={492}
                    height={289}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your name or business name, complete address, and TIN
                  (SSN if you&apos;re an individual; EIN if you&apos;re a
                  business)
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 2,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-recipient-img.webp" />
                  <source srcSet="/assets/images/1099-b-recipient-img.png" />
                  <img
                    src="/assets/images/1099-b-recipient-img.png"
                    loading="lazy"
                    alt="Form 1099-b-recipient details"
                    width={495}
                    height={456}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                  Enter the recipient’s name, complete address, and TIN (SSN for
                  an individual; EIN for a business).
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b> Account number:</b> You must provide the account number
                    in these two situations:
                    <ol style={{ marginBottom: 1, listStyleType: 'circle' }}>
                      <li style={{ marginBottom: 16 }}>
                        You have multiple accounts for the same recipient, and
                        you are filing more than one Form 1099-B for that
                        recipient.
                      </li>
                      <li style={{ marginBottom: 16 }}>
                        You checked the &quot;FATCA filing requirement&quot;
                        box.
                      </li>
                    </ol>
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    <b> 2nd TIN not:</b> If the IRS has notified you two times
                    within the last 3 years that the payee provided an incorrect
                    TIN, you can enter an &quot;X&quot; in this box. By doing
                    so, the IRS will stop sending you further notices about this
                    account&apos;s TIN.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  CUSIP Number
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, mb: 3 }}>
                  For transactional reporting by brokers, enter the CUSIP
                  (Committee on Uniform Security Identification Procedures)
                  number of the security or other applicable identifying number.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  FATCA Filing Requirement Checkbox
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if you are a
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Foreign Financial Institution (FFI) reporting payments to a
                    U.S. account pursuant to an election described in
                    Regulations section 1.1471-4(d)(5)(i)(A).
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    U.S. payer that is reporting on Form 1099-B as part of
                    satisfying your requirement to report with respect to a U.S.
                    account for chapter 4 purposes as described in Regulations
                    section 1.1471-4(d)(2)(iii)(A).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Applicable Checkbox on Form 8949
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-applicable-img.webp" />
                  <source srcSet="/assets/images/1099-b-applicable-img.png" />
                  <img
                    src="/assets/images/1099-b-applicable-img.png"
                    loading="lazy"
                    alt="Form 1099-b- applicable checkbox on Form 8949"
                    width={314}
                    height={118}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Use this box to enter a one-letter code to assist the
                  recipient in properly reporting the transaction on Form 8949
                  and/or Schedule D (Form 1040).
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 10 }}>
                    <b>Code A :</b> Short-term transaction for which cost basis
                    is reported to the IRS. Recipient reports on Schedule D line
                    1a or Form 8949 Box A 1 {'>'} Schedule D line 1b.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Code B :</b> Short-term transaction for which cost basis
                    is not reported to the IRS. Recipient reports on Form 8949
                    Box B {'>'} Schedule D line 2.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Code D :</b> Long-term transaction for which cost basis
                    is reported to the IRS. Recipient reports on Schedule D line
                    8a or Form 8949 Box D {'>'} Schedule D line 8b.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    {' '}
                    <b>Code E :</b> Long-term transaction for which cost basis
                    is not reported to the IRS. Recipient reports on Form 8949
                    Box E {'>'} Schedule D line 9.
                  </li>
                  <li style={{ marginBottom: 10 }}>
                    <b>Code X :</b> Use when holding period is unknown, so
                    cannot determine if Box B or E should be checked on Form
                    8949.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1a: Description of Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-description-property-img.webp" />
                  <source srcSet="/assets/images/1099-b-description-property-img.png" />
                  <img
                    src="/assets/images/1099-b-description-property-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1a-description of property"
                    width={677}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  For stocks and debt instruments:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the issuer&apos;s name
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Enter the number of shares/units exchanged
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For stocks, also enter the class (e.g. preferred, common)
                    abbreviated as P, C, or O (for others)
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For non-Section 1256 options or securities futures contracts:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter the name of the underlying asset
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Enter the number of shares/units covered by the contract
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For bartering transactions:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Describe the service or property provided
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For regulated futures contracts and forward contracts:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Enter &quot;RFC&quot; or other appropriate brief description
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1b: Date Acquired
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-date-acquired-img.webp" />
                  <source srcSet="/assets/images/1099-b-date-acquired-img.png" />
                  <img
                    src="/assets/images/1099-b-date-acquired-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1b-date acquired"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the date you acquired the securities that were sold.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  However, leave this box blank if:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    The securities sold were acquired on multiple different
                    dates
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    You checked Box 5 (indicating the securities were
                    non-covered) and chose not to complete Box 1b
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1c: Date Sold or Disposed
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-date-sold-disposed-img.webp" />
                  <source srcSet="/assets/images/1099-b-date-sold-disposed-img.png" />
                  <img
                    src="/assets/images/1099-b-date-sold-disposed-img.png"
                    loading="lazy"
                    alt="Form 1099-b- box-1c-date sold or disposed"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    For broker transactions, enter the trade date of the sale or
                    exchange.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For barter exchanges, enter the date that cash, property,
                    credit, or scrip is actually or constructively received.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    mt: 3,
                    fontWeight: 600,
                  }}
                >
                  Box 1d: Proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-proceeds-img.webp" />
                  <source srcSet="/assets/images/1099-b-proceeds-img.png" />
                  <img
                    src="/assets/images/1099-b-proceeds-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1d-proceeds"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross cash proceeds received from all
                  dispositions (including short sales) of securities,
                  commodities, options, securities futures contracts, or forward
                  contracts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Show any losses, such as from closing a written option or
                  forward contract, as a negative amount by enclosing it in
                  parentheses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You must reduce the proceeds by any commissions and transfer
                  taxes related to the sale.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  For securities sold due to the exercise of an option:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Pre-2014 options: You may, but are not required to, adjust
                    for option premiums if consistent with your books. Check the
                    box in Box 6 accordingly.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Post-2013 options: See Regulations section 1.6045-1(m) for
                    treatment of premiums.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If selling identical stock via a single trade order on the
                  same day, you can average the proceeds per share, unless the
                  customer notifies intent to use actual proceeds per share by
                  Jan 15th of the following year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT include any accrued qualified stated interest on bonds
                  sold. Report that interest on Form 1099-INT instead.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 1e: Cost or Other Basis
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-cost-other-basis-img.webp" />
                  <source srcSet="/assets/images/1099-b-cost-other-basis-img.png" />
                  <img
                    src="/assets/images/1099-b-cost-other-basis-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1e-cost or other basis"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the adjusted cost basis of any securities sold, unless:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    The security is non-covered (meaning you checked Box 5)
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If you checked Box 5 indicating the securities are
                    non-covered and you are not reporting basis, leave Box 1e
                    blank
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Only enter &quot;-0-&quot; in Box 1e if the securities
                  actually had a zero cost-basis.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 1f: Accrued Market Discount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-accrued-market-img.webp" />
                  <source srcSet="/assets/images/1099-b-accrued-market-img.png" />
                  <img
                    src="/assets/images/1099-b-accrued-market-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1f-accured market discount"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of accrued market discount.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 1g: Wash sale loss disallowed
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-wash-sale-img.webp" />
                  <source srcSet="/assets/images/1099-b-wash-sale-img.png" />
                  <img
                    src="/assets/images/1099-b-wash-sale-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-1g-wash sale loss disallowed"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any losses that were disallowed due to the wash sale
                  rule. The wash sale rule applies when you sell a security at a
                  loss and purchase the same or substantially identical security
                  within 30 days before or after the sale.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You must report disallowed losses if both the sale and
                  purchase transactions occurred in the same account and
                  involved covered securities with the same CUSIP number.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 2: Type of Gain or Loss
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-short-term-img.webp" />
                  <source srcSet="/assets/images/1099-b-short-term-img.png" />
                  <img
                    src="/assets/images/1099-b-short-term-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-2-type of gain or loss"
                    width={247}
                    height={90}
                    className="proportional-img"
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate whether the gain or loss is short-term or long-term,
                  and whether any portion of the gain or loss is ordinary income
                  or loss. To make this determination, follow these steps:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Consider any information reported on a transfer statement or
                    Form 8937.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Apply the rules for stock acquired from a decedent or as a
                    gift.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    If a customer acquired securities that caused a loss from a
                    sale of other securities to be disallowed under the wash
                    sale rule (reported in box 5 of a 2013 or earlier Form
                    1099-B, or box 1g of a 2014 or 2015 Form 1099-B), use the
                    rules in section 1223(3) to determine the holding period of
                    the acquired securities.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    For short sales, report whether any gain or loss on the
                    closing of the short sale is short-term or long-term based
                    on the acquisition date of the security delivered to close
                    the short sale. Apply the rule in section 1233(d), if
                    applicable.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,

                    fontWeight: 600,
                  }}
                >
                  Box 3: Check if Proceeds Are From Collectibles or From a QOF
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-check-collectibles-img.webp" />
                  <source srcSet="/assets/images/1099-b-check-collectibles-img.png" />
                  <img
                    src="/assets/images/1099-b-check-collectibles-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-3- check if proceeds are from collectibles or from a qof"
                    width={247}
                    height={90}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    Check the &quot;Collectibles&quot; box if the proceeds you
                    are reporting in box 1d are from a transaction involving
                    collectibles.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Check the &quot;QOF&quot; box if you are reporting a
                    disposition of an interest in a QOF.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-federal-income-img.webp" />
                  <source srcSet="/assets/images/1099-b-federal-income-img.png" />
                  <img
                    src="/assets/images/1099-b-federal-income-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-4-federal income withheld"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report any federal income tax withheld from payments reported.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Withholding is required if recipients have not
                  provided their TIN.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5. Check if a Noncovered Security
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-noncovered-security-img.webp" />
                  <source srcSet="/assets/images/1099-b-noncovered-security-img.png" />
                  <img
                    src="/assets/images/1099-b-noncovered-security-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-5-check if a noncovered security"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the box if you’re reporting the sale of a noncovered
                  security.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT check this box if reporting the sale of a covered
                  security.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Reported to IRS
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-reported-irs-img.webp" />
                  <source srcSet="/assets/images/1099-b-reported-irs-img.png" />
                  <img
                    src="/assets/images/1099-b-reported-irs-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-6-reported to IRS"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Box 6 relates to how the broker reports the sale proceeds to
                  the IRS.
                </Typography>
                <ol>
                  <li style={{ marginBottom: 15 }}>Gross Proceeds checkbox:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 15 }}>
                      Check this box if you&apos;re reporting the gross proceeds
                      from the sale.
                    </li>
                    <li style={{ marginBottom: 15 }}>
                      Gross proceeds typically include the total amount received
                      from the sale before any deductions.
                    </li>
                  </ol>
                  <li style={{ marginBottom: 15 }}>Net Proceeds checkbox:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 15 }}>
                      Check this box if you&apos;re reporting the net proceeds
                      from the sale.
                    </li>
                    <li style={{ marginBottom: 15 }}>
                      Net proceeds are the amount after subtracting commissions
                      and option premiums from the gross proceeds.
                    </li>
                  </ol>
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b>
                  <ul>
                    <li>Only one box should be checked, not both</li>
                    <li>
                      The reporting method should be consistent with what&apos;s
                      shown in Box 1d (Proceeds).
                    </li>
                  </ul>
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Checked if Loss Not Allowed Based on Amount in Box 1d
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-check-if-loss-img.webp" />
                  <source srcSet="/assets/images/1099-b-check-if-loss-img.png" />
                  <img
                    src="/assets/images/1099-b-check-if-loss-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-7-checked if loss not allowed based on amount in box 1d"
                    width={243}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Box 7 is used to indicate a specific situation where a loss
                  cannot be claimed by the taxpayer. This box is checked when a
                  loss cannot be claimed based on the amount reported in Box 1d
                  (Proceeds).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if the proceeds (Box 1d) are:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 12 }}>
                    From a reportable change in control or capital structure
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Received from a corporation and cannot be treated as a loss
                    by the taxpayer
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Profit or (Loss) Realized in 2025 on Closed Contracts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-profit-loss-contracts-img.webp" />
                  <source srcSet="/assets/images/1099-b-profit-loss-contracts-img.png" />
                  <img
                    src="/assets/images/1099-b-profit-loss-contracts-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-8-profit or loss realized in 2025 on closed contracts"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the profit or (loss) realized by the customer on closed
                  regulated futures, foreign currency, or Section 1256 option
                  contracts in 2025.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Unrealized Profit or (Loss) on Open
                  Contracts—12/31/2024
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-unrealized-profit-or-loss-img.webp" />
                  <source srcSet="/assets/images/1099-b-unrealized-profit-or-loss-img.png" />
                  <img
                    src="/assets/images/1099-b-unrealized-profit-or-loss-img.png"
                    loading="lazy"
                    alt="Form 1099-b- box-9: unrealized profit or loss on open contracts-12/31/2024"
                    width={245}
                    height={90}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the unrealized profit or (loss) on open regulated
                  futures, foreign currency, or Section 1256 option contracts at
                  the end of 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  DO NOT include amounts related to contracts that were open on
                  December 31, 2024, and were transferred to another broker
                  during 2025.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10. Unrealized Profit or (Loss) on Open
                  Contracts—12/31/2025
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-unrealized-open-contracts-img.webp" />
                  <source srcSet="/assets/images/1099-b-unrealized-open-contracts-img.png" />
                  <img
                    src="/assets/images/1099-b-unrealized-open-contracts-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-10-unrealized profit or loss on open contracts-12/31/2025"
                    width={245}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the unrealized profit or (loss) on open regulated
                  futures, foreign currency, or Section 1256 option contracts at
                  the end of 2025.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Aggregate Profit or (Loss) on Contracts
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-aggregate-profit-img.webp" />
                  <source srcSet="/assets/images/1099-b-aggregate-profit-img.png" />
                  <img
                    src="/assets/images/1099-b-aggregate-profit-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-11- aggregate profit or loss on contracts"
                    width={244}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the aggregate profit or (loss) for the year from
                  regulated futures, foreign currency, or Section 1256 option
                  contracts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Use boxes 8–10 to calculate the aggregate profit or (loss).
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Check if Basis Reported to IRS
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-basic-reported-irs-img.webp" />
                  <source srcSet="/assets/images/1099-b-basic-reported-irs-img.png" />
                  <img
                    src="/assets/images/1099-b-basic-reported-irs-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-12-check if basis reported to IRS"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check this box if:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>
                    You are not checking box 5, or
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    You are checking box 5 but are reporting basis to the IRS in
                    box 1e anyway
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Bartering
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-batering-img.webp" />
                  <source srcSet="/assets/images/1099-b-batering-img.png" />
                  <img
                    src="/assets/images/1099-b-batering-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-13-bartering"
                    width={245}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }} component="div">
                  Enter the total value received by a member or client of a
                  barter exchange. This includes:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 12 }}>Cash received</li>
                  <li style={{ marginBottom: 12 }}>
                    The fair market value of any property or services received
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    The fair market value of any trade credits or scrip credited
                    to the member&apos;s or client&apos;s account
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 14–16: State information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/1099-b-state-name-tax-img.webp" />
                  <source srcSet="/assets/images/1099-b-state-name-tax-img.png" />
                  <img
                    src="/assets/images/1099-b-state-name-tax-img.png"
                    loading="lazy"
                    alt="Form 1099-b-box-14-16-state information"
                    width={591}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 14:</b> Enter the 2-letter state abbreviation for the
                  state
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  <b> Box 15:</b> Enter the state identification number
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b> Box 16:</b> Report any state income tax withheld from the
                  payment
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to File Form 1099-B
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can file Form 1099-B, either electronically or by mail.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic Filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing is the IRS-recommended method for its efficiency,
                  accuracy, and prompt processing notifications.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  With TaxZerone, you can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-file Form 1099-B
                  </Link>{' '}
                  complete within minutes. Here&apos;s how to works:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter the required information, including:
                  </li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Payer&apos;s name, address, and Tax Identification Number
                      (TIN)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Recipient&apos;s name, address, and TIN
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Transaction details (date of sale, proceeds, cost basis,
                      etc.)
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    Review all entered information for accuracy.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Transmit the form electronically to the IRS.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Provide a copy to the recipient.
                  </li>
                </ol>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  While less efficient, paper filing remains an option for those
                  who prefer it.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Steps for paper filing:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download Form 1099-B from the official{' '}
                    <Link
                      href="https://www.irs.gov"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS website
                    </Link>
                    .
                  </li>
                  <li style={{ marginBottom: 16 }}>Print the form.</li>
                  <Alert
                    severity="warning"
                    icon={false}
                    sx={{
                      mb: 3,
                      mt: 0,
                      borderLeft: '3px solid rgb(102, 60, 0)',
                    }}
                  >
                    <b> Note:</b> The IRS requires special red ink forms for
                    submission. You can order these from the IRS or approved
                    vendors.
                  </Alert>
                  <li style={{ marginBottom: 16 }}>
                    Complete all required fields using black ink.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Make a copy for your records.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the original form to the IRS address specified in the
                    Form 1099 General Instructions. The{' '}
                    <Link
                      href="#1099-b-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      mailing address
                    </Link>{' '}
                    varies based on your state.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Send a copy to the recipient by February 15th of the year
                    following the reportable transaction.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important considerations for paper filing
                  </Typography>
                  <ul style={{ marginBottom: 24 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks before the deadline to ensure
                      timely arrival.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Use certified mail for proof of timely filing.
                    </li>
                    <li style={{ marginBottom: 0 }}>
                      Ensure all copies are legible.
                    </li>
                  </ul>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  As an alternative, you can choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                    id="1099-b-mailing-address"
                  >
                    file Form 1099-B electronically
                  </Link>{' '}
                  for faster processing and delivery, reducing the risk of
                  delays or lost forms.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-B - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you prefer paper filing, the mailing address for Form
                  1099-B varies depending on your business location. Below is a
                  table summarizing the mailing address for Form 1099-B:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-B to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Internal Revenue Service
                            <br />
                            Austin Submission Processing Center
                            <br />
                            P.O. Box 149213
                            <br />
                            Austin, TX 78714
                            <br />
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            P.O. Box 219256
                            <br /> Kansas City, MO 64121-9256
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury
                            <br />
                            IRS Submission Processing Center
                            <br />
                            1973 North Rulon White Blvd.
                            <br />
                            Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Internal Revenue Service,
                            <br /> Austin Submission Processing Center,
                            <br /> P.O. Box 149213, <br />
                            Austin, TX 78714
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to E-File Form 1099-B with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before you start{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing Form 1099-B
                  </Link>
                  , ensure you have all the necessary information to facilitate
                  a smooth and efficient filing experience. Here&apos;s what you
                  need to gather:
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Required information for filing Form 1099-B:
                </Typography>
                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>Taxpayer information:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Name, address, and taxpayer identification number (TIN) of
                      the recipient
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Name, address, and TIN of the payer (broker)
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Transaction details:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Description of the property sold
                    </li>
                    <li style={{ marginBottom: 16 }}>Date acquired</li>
                    <li style={{ marginBottom: 16 }}>Date sold or disposed</li>
                    <li style={{ marginBottom: 16 }}>Sales price</li>
                    <li style={{ marginBottom: 16 }}>Cost or other basis</li>
                    <li style={{ marginBottom: 16 }}>Amount of gain or loss</li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>Additional information:</li>
                  <ol style={{ listStyleType: 'circle' }}>
                    <li style={{ marginBottom: 16 }}>
                      Whether the basis was reported to the IRS
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Whether the gain or loss was short-term or long-term
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Any federal income tax withheld
                    </li>
                  </ol>
                  <li style={{ marginBottom: 16 }}>
                    CUSIP number (for securities): A unique identifier for most
                    financial instruments
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Steps to e-file Form 1099-B using TaxZerone:
                </Typography>

                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <b>Step 1: Complete Form 1099-B</b>
                  <br />
                  Open Form 1099-B and fill out the necessary fields with the
                  information you have gathered, including payer details,
                  recipient details, transaction details, and any federal income
                  tax withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Step 2: Review and transmit the return</b>
                  <br />
                  Carefully review all the entered information to ensure
                  accuracy. Once verified, transmit the completed Form 1099-B to
                  the IRS electronically through TaxZerone&apos;s secure
                  platform.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  <b>Step 3: Provide the recipient copy</b>
                  <br />
                  After successfully transmitting the form to the IRS, send a
                  copy of the completed Form 1099-B to the recipient. This
                  ensures they can accurately report the income on their tax
                  return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Benefits of E-Filing Form 1099-B with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing Form 1099-B
                  </Link>{' '}
                  with TaxZerone offers several advantages that streamline the
                  filing process and ensure compliance with IRS requirements:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS Form Validations:
                      </span>{' '}
                      TaxZerone&apos;s system checks your returns for errors or
                      missing information to ensure accuracy before submission.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Email Recipient Copies:
                      </span>{' '}
                      Conveniently send recipient copies of Form 1099-B via
                      email and eliminate manual mailing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Secure Portal Access (ZeroneVault):
                      </span>{' '}
                      Recipients can access their return copies anytime,
                      anywhere through the secure ZeroneVault portal.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Competitive Pricing:
                      </span>{' '}
                      TaxZerone offers industry-leading pricing, with rates
                      starting as low as $0.59 per form, making it affordable
                      for businesses of all sizes.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1">
                  By using TaxZerone&apos;s e-filing solution, you can
                  streamline your{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-b/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1099-B filing
                  </Link>
                  . Save time, reduce errors, and provide a better experience
                  for your recipients.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 0 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 30, md: 26, sm: 22, xs: 18 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Maximize the efficiency of your tax reporting by{' '}
              <Hidden smDown>
                <br />
              </Hidden>
              choosing TaxZerone for e-filing Form 1099-B.
            </Typography>
            <Box
              component="div"
              sx={{
                display: 'grid',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/joeJWOAuigM?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: { xs: 3, sm: 4, md: 0 },
                    mb: { xs: 0, sm: 0, md: 10 },
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099binstructions`}
                >
                  E-file Form 1099-B
                </Button>
              </Box>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099BInstructions;
